import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
  Dialog,
  DialogContent,
  IconButton,
} from "@material-ui/core";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import GetAppRoundedIcon from "@material-ui/icons/GetAppRounded";
import OpacityRoundedIcon from "@material-ui/icons/OpacityRounded";
import AssessmentRoundedIcon from "@material-ui/icons/AssessmentRounded";
import ImageRoundedIcon from "@material-ui/icons/ImageRounded";
import LocalFloristRoundedIcon from "@material-ui/icons/LocalFloristRounded";
import FaceRoundedIcon from "@material-ui/icons/FaceRounded";
import FavoriteRoundedIcon from "@material-ui/icons/FavoriteRounded";
import SpaRoundedIcon from "@material-ui/icons/SpaRounded";
import LibraryBooksRoundedIcon from "@material-ui/icons/LibraryBooksRounded";
import PictureAsPdfRoundedIcon from "@material-ui/icons/PictureAsPdfRounded";
import ExpandLessRoundedIcon from "@material-ui/icons/ExpandLessRounded";
import useCommonStyles from "./style";
import PdfReportViewer from "./PdfReportViewer";
import ReportHighlights from "./ReportHighlights";
import ReportDisplay from "./ReportDisplay";
import ReportInputViewer from "./ReportInputViewer";
import {
  getFreshReportUrlRequest,
  getChatbotReportUrlRequest,
  getReportInputUrlRequest,
} from "../../../PatientApiActions";

// Strapi collection slug backing each PDF-based report preview.
const REPORT_COLLECTIONS = {
  "ayurveda-blood": "clinical-blood-reports",
  tongue: "tongue-reports",
  nadi: "nadi-reports",
  prakriti: "prakriti-reports",
  comprehensive: "comprehensive-health-assessments",
};

// Preview keys backed by a chatbot report (by-user endpoint) instead of Strapi,
// mapped to their chatbot report_type.
const REPORT_CHATBOT_TYPES = {
  symptom: "symptom_analyzer",
};

// The blood report is generated from a lab report the patient uploads; that
// upload is what the "Blood Reports" card shows, resolved via this collection's
// report_id.
const BLOOD_INPUT_COLLECTION = "clinical-blood-reports";

// Imaging documents whose analysis type reads as a blood report belong under
// the "Blood Reports" card rather than "Imaging & Other Reports".
const isBloodDocument = (item) =>
  !!item.title && item.title.toLowerCase().includes("blood");

// Preview opens the existing report component unchanged in a dialog; categories
// without a backend keep their actions disabled. No report body is rendered on
// the page directly and no API calls are altered.
const ReportsLauncher = ({ patient, imageAnalysisReportList = [] }) => {
  const classes = useCommonStyles();
  const dispatch = useDispatch();
  const [activeReport, setActiveReport] = useState(null);
  // Reports open on their highlights; the full PDF is revealed on demand.
  const [showFullPdf, setShowFullPdf] = useState(false);

  const openPreview = (key) => {
    setShowFullPdf(false);
    setActiveReport(key);
  };

  // Strapi records key reports on a plain 10-digit phone; patient.mobile is
  // stored with a country-code prefix (e.g. "+91XXXXXXXXXX"), so take the last
  // 10 digits to match patient_phone.
  const mobileDigits = (patient?.mobile || "").replace(/\D/g, "");
  const patientPhone =
    mobileDigits.length > 10 ? mobileDigits.slice(-10) : mobileDigits;

  // Split uploaded imaging documents: blood-report PDFs surface under the
  // "Blood Reports" card, everything else under "Imaging & Other Reports".
  const bloodDocuments = imageAnalysisReportList.filter(isBloodDocument);
  const otherDocuments = imageAnalysisReportList.filter(
    (item) => !isBloodDocument(item)
  );

  // Open a URL in a new tab (original behavior — used as a fallback when the
  // file cannot be fetched as a blob, e.g. blocked by CORS).
  const openInNewTab = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener";
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download a single file. Fetching as a blob lets us trigger a real save and
  // download multiple files reliably (looping new-tab opens gets popup-blocked,
  // so only the first file would open).
  const downloadFile = async (url, filename) => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // CORS / network issue — fall back to opening the file in a new tab.
      openInNewTab(url, filename);
    }
  };

  // With multiple documents, open the same popup as Preview so the user can
  // pick a file (each row has its own preview/download). With a single
  // document, download it directly.
  const handleDownloadDocuments = (documents, previewKey) => {
    if (documents.length > 1) {
      setActiveReport(previewKey);
    } else if (documents.length === 1) {
      downloadFile(documents[0].url, documents[0].title);
    }
  };

  // Re-sign a fresh, temporary PDF link for the patient's latest report in this
  // collection (via the DXAI service, keyed by report_id) and download it. No
  // report / un-signable report resolves to no url, so nothing is downloaded.
  const handleDownloadStrapiReport = (collection) => {
    dispatch(getFreshReportUrlRequest(collection, patientPhone))
      .then((res) => {
        if (res && res.url) {
          downloadFile(res.url, `${collection}.pdf`);
        }
      })
      .catch(() => {});
  };

  // Download a chatbot-sourced report's (e.g. Symptom Analyzer) signed PDF.
  const handleDownloadChatbotReport = (type) => {
    dispatch(getChatbotReportUrlRequest(patientPhone, type))
      .then((res) => {
        if (res && res.url) {
          downloadFile(res.url, `${type}.pdf`);
        }
      })
      .catch(() => {});
  };

  // Blood Reports downloads the uploaded lab report the blood report was
  // generated from — a PDF or an image, so keep the stored file's own name and
  // extension. With none stored, it falls back to the patient's uploaded blood
  // documents so the button keeps working as before.
  const handleDownloadBloodInput = () => {
    dispatch(getReportInputUrlRequest(BLOOD_INPUT_COLLECTION, patientPhone))
      .then((res) => {
        if (res && res.available && res.url) {
          downloadFile(res.url, res.fileName || "lab-report");
          return;
        }
        handleDownloadDocuments(bloodDocuments, "blood");
      })
      .catch(() => handleDownloadDocuments(bloodDocuments, "blood"));
  };

  const reports = [
    {
      title: "Blood Reports",
      subtitle: "Laboratory reports uploaded for this patient.",
      icon: <OpacityRoundedIcon />,
      accent: "#ef5350",
      tint: "#fdecea",
      previewKey: "blood",
      onDownload: handleDownloadBloodInput,
    },
    {
      title: "Symptom Analyzer",
      subtitle: "Symptom analysis and assessment summary.",
      icon: <AssessmentRoundedIcon />,
      accent: "#7e57c2",
      tint: "#f3edfb",
      previewKey: "symptom",
      onDownload: () => handleDownloadChatbotReport("symptom_analyzer"),
    },
    {
      title: "Imaging & Other Reports",
      subtitle: "Uploaded scans, images and documents.",
      icon: <ImageRoundedIcon />,
      accent: "#26a69a",
      tint: "#e3f4f2",
      previewKey: "imaging",
      onDownload: () => handleDownloadDocuments(otherDocuments, "imaging"),
    },
    {
      title: "Ayurveda-Correlated Blood Report Summary",
      subtitle: "Ayurvedic correlation of blood markers.",
      icon: <LocalFloristRoundedIcon />,
      accent: "#66bb6a",
      tint: "#e8f5e9",
      previewKey: "ayurveda-blood",
      onDownload: () => handleDownloadStrapiReport("clinical-blood-reports"),
    },
    {
      title: "Tongue Analysis (Jihva Pariksha)",
      subtitle: "Tongue examination findings.",
      icon: <FaceRoundedIcon />,
      accent: "#ec407a",
      tint: "#fce4ec",
      previewKey: "tongue",
      onDownload: () => handleDownloadStrapiReport("tongue-reports"),
    },
    {
      title: "Nadi Report",
      subtitle: "Pulse diagnosis report.",
      icon: <FavoriteRoundedIcon />,
      accent: "#ef5350",
      tint: "#fdecea",
      previewKey: "nadi",
      onDownload: () => handleDownloadStrapiReport("nadi-reports"),
    },
    {
      title: "Prakriti Report",
      subtitle: "Constitutional assessment report.",
      icon: <SpaRoundedIcon />,
      accent: "#26c6da",
      tint: "#e0f7fa",
      previewKey: "prakriti",
      onDownload: () => handleDownloadStrapiReport("prakriti-reports"),
    },
    {
      title: "Comprehensive Health Report",
      subtitle: "Combined report of all completed assessments.",
      icon: <LibraryBooksRoundedIcon />,
      accent: "#5c6bc0",
      tint: "#e8eaf6",
      // Preview is intentionally off for this report — download only. Restoring
      // it is a matter of setting previewKey back to "comprehensive" (its
      // collection and dialog title are still mapped below).
      previewKey: null,
      onDownload: () =>
        handleDownloadStrapiReport("comprehensive-health-assessments"),
    },
  ];

  const previewTitles = {
    blood: "Blood Reports",
    symptom: "Symptom Analyzer",
    imaging: "Imaging & Other Reports",
    "ayurveda-blood": "Ayurveda-Correlated Blood Report Summary",
    tongue: "Tongue Analysis (Jihva Pariksha)",
    nadi: "Nadi Report",
    prakriti: "Prakriti Report",
    comprehensive: "Comprehensive Health Report",
  };
  const closePreview = () => setActiveReport(null);

  // Preview body: highlights first, full PDF on demand. Works for a Strapi
  // collection or a chatbot report type.
  const renderHighlightsPreview = ({ collection, chatbotType }) => {
    const source = chatbotType ? { chatbotType } : { collection };
    return (
      <Box>
        <ReportHighlights phone={patientPhone} {...source} />
        <Box display="flex" justifyContent="center" mt={2}>
          <Button
            variant="outlined"
            size="small"
            className={classes.hlFullBtn}
            startIcon={
              showFullPdf ? <ExpandLessRoundedIcon /> : <PictureAsPdfRoundedIcon />
            }
            onClick={() => setShowFullPdf((v) => !v)}
          >
            {showFullPdf ? "Hide full report" : "View full report (PDF)"}
          </Button>
        </Box>
        {showFullPdf && (
          <Box mt={2}>
            <PdfReportViewer phone={patientPhone} {...source} />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Grid container spacing={6}>
        {reports.map((r) => (
          <Grid item xs={12} sm={6} md={3} key={r.title}>
            <Card className={classes.reportCard} elevation={0}>
              <Box className={classes.reportCardHeader}>
                <Box
                  className={classes.reportCardIcon}
                  style={{ color: r.accent, backgroundColor: r.tint }}
                >
                  {r.icon}
                </Box>
                <Typography className={classes.reportCardTitle}>
                  {r.title}
                </Typography>
              </Box>
              <Typography className={classes.reportCardSubtitle}>
                {r.subtitle}
              </Typography>
              <Box className={classes.reportCardActions}>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<VisibilityRoundedIcon />}
                  className={classes.reportPreviewBtn}
                  disabled={!r.previewKey}
                  onClick={() => r.previewKey && openPreview(r.previewKey)}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppRoundedIcon />}
                  className={classes.reportDownloadBtn}
                  disabled={!r.onDownload}
                  onClick={() => r.onDownload && r.onDownload()}
                >
                  Download
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={!!activeReport} onClose={closePreview} maxWidth="md" fullWidth>
        <Box className={classes.reportDialogTitle}>
          <Typography className={classes.reportDialogTitleText}>
            {previewTitles[activeReport] || ""}
          </Typography>
          <IconButton aria-label="close" onClick={closePreview} size="small">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <DialogContent dividers>
          {REPORT_COLLECTIONS[activeReport] &&
            renderHighlightsPreview({
              collection: REPORT_COLLECTIONS[activeReport],
            })}

          {REPORT_CHATBOT_TYPES[activeReport] &&
            renderHighlightsPreview({
              chatbotType: REPORT_CHATBOT_TYPES[activeReport],
            })}

          {activeReport === "blood" && (
            <ReportInputViewer
              phone={patientPhone}
              collection={BLOOD_INPUT_COLLECTION}
              emptyTitle="No lab report uploaded"
              emptyNote="No laboratory report has been uploaded for this patient's blood report yet."
              fallback={
                bloodDocuments.length ? (
                  <ReportDisplay data={bloodDocuments} />
                ) : null
              }
            />
          )}

          {activeReport === "imaging" && (
            <ReportDisplay data={otherDocuments} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReportsLauncher;
